import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Field, Input } from "../../components/ui";
import { cognitoConfig, confirmForgotPassword, forgotPassword, signIn } from "../../lib/cognito";
import { messageFor } from "../../lib/cognitoErrors";
import { checkPassword } from "../../lib/passwordPolicy";
import { store } from "../../lib/session";
import { AuthAlert, AuthCard, AuthLink, AuthNote, CodeField, PasswordChecklist } from "./parts";

/**
 * Reset a forgotten password: request a code, then set the new one. Cognito
 * answers the request the same way whether or not the address is registered, so
 * this page never reveals who has an account.
 */
export function ForgotPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [step, setStep] = useState<"request" | "reset">("request");
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const strong = checkPassword(password).valid;

  const onRequest = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await forgotPassword(await cognitoConfig(), email);
      setStep("reset");
    } catch (err) {
      setError(messageFor(err).message);
    } finally {
      setPending(false);
    }
  };

  const onReset = async (e: FormEvent) => {
    e.preventDefault();
    if (!strong) return;
    setPending(true);
    setError(null);
    try {
      const cfg = await cognitoConfig();
      await confirmForgotPassword(cfg, email, code, password);
      // The new password is right here; signing in beats sending the coach back
      // to a form to type it a second time.
      store(await signIn(cfg, email, password));
      navigate("/me", { replace: true });
    } catch (err) {
      setError(messageFor(err).message);
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthCard
      title={step === "request" ? "Reset your password" : "Set a new password"}
      subtitle={
        step === "request"
          ? "We'll email you a six-digit code."
          : "Enter the code we emailed you and pick a new password."
      }
      footer={
        <>
          Remembered it? <AuthLink to="/login">Sign in</AuthLink>
        </>
      }
    >
      {step === "reset" ? (
        <AuthNote>If {email} has an account, a code is on its way.</AuthNote>
      ) : null}
      {error ? <AuthAlert>{error}</AuthAlert> : null}

      {step === "request" ? (
        <form onSubmit={(e) => void onRequest(e)} className="space-y-4">
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              required
            />
          </Field>

          <Button type="submit" size="lg" disabled={pending} className="w-full">
            {pending ? "Sending…" : "Send code"}
          </Button>
        </form>
      ) : (
        <form onSubmit={(e) => void onReset(e)} className="space-y-4">
          <Field label="Verification code">
            <CodeField value={code} onChange={setCode} />
          </Field>

          <Field label="New password">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>

          <PasswordChecklist password={password} />

          <Button
            type="submit"
            size="lg"
            disabled={pending || !strong || code.length < 6}
            className="w-full"
          >
            {pending ? "Saving…" : "Set password and sign in"}
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
